import { findChildrenByType, NodeWithPos } from 'prosemirror-utils';
import { TableRect, selectedRect } from 'prosemirror-tables';
import { ResolvedPos, Node as ProsemirrorNode, Schema, Slice } from 'prosemirror-model';
import { Transaction, NodeSelection, TextSelection, EditorState } from 'prosemirror-state';
import { IAlignList } from '@/editor/types';
import { EProsemirrorType } from '@/editor/types/enums';

export class ProsemirrorHelper {
  static selectNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(NodeSelection.create(transaction.doc, position));
  }

  static selectTextNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(TextSelection.create(transaction.doc, position));
  }

  static selectTextNearPosition(transaction: Transaction, position: number): Transaction {
    const resolvedPosition = transaction.doc.resolve(position);
    return transaction.setSelection(TextSelection.near(resolvedPosition));
  }

  static deleteNode(transaction: Transaction, position: number, nodeSize: number): Transaction {
    return transaction.delete(position, position + nodeSize);
  }

  static createEditorStateFromSlice(slice: Slice, schema: Schema): EditorState {
    const state: EditorState = EditorState.create({ schema });
    const transaction: Transaction = state.tr.replace(0, state.doc.content.size, slice);
    return state.apply(transaction);
  }

  static isCompleteTableSelected(state: EditorState): boolean {
    const { left, right, map, top, bottom }: TableRect = selectedRect(state);
    return left === 0 && right === map.width && top === 0 && map.height === bottom;
  }

  static getNodeAttributesFromPosition(
    transaction: Transaction,
    position: number
  ): ProsemirrorNode['attrs'] {
    const node: ProsemirrorNode = transaction.doc.nodeAt(position);

    if (!node) {
      return;
    }

    return node.attrs;
  }

  static getNodeBeforePosition(transaction: Transaction, position: number): ProsemirrorNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeBefore;
  }

  static getNodeAfterPosition(transaction: Transaction, position: number): ProsemirrorNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeAfter;
  }

  static updateNodeAttributes(
    transaction: Transaction,
    schema: Schema,
    position: number,
    nodeType: EProsemirrorType,
    attributes: ProsemirrorNode['attrs']
  ): Transaction {
    const attrs: ProsemirrorNode['attrs'] = ProsemirrorHelper.getNodeAttributesFromPosition(
      transaction,
      position
    );

    if (!attrs) {
      return;
    }

    return transaction.setNodeMarkup(position, schema.nodes[nodeType], {
      ...attrs,
      ...attributes,
    });
  }

  static alignList(
    state: EditorState,
    { align, position }: IAlignList,
    type: EProsemirrorType
  ): Transaction {
    const { tr, schema, doc } = state;
    const listNode: ProsemirrorNode = doc.nodeAt(position);

    if (!listNode) {
      return;
    }

    const paragraphs: NodeWithPos[] = findChildrenByType(
      listNode,
      schema.nodes[EProsemirrorType.Paragraph]
    );
    const headings: NodeWithPos[] = findChildrenByType(
      listNode,
      schema.nodes[EProsemirrorType.Heading]
    );

    [...paragraphs, ...headings].forEach(({ pos, node }: NodeWithPos) => {
      const nodePosition = position + pos + 1;
      const nodeAttributes = ProsemirrorHelper.getNodeAttributesFromPosition(tr, nodePosition);

      tr.setNodeMarkup(nodePosition, schema.nodes[node.type.name], { ...nodeAttributes, align });
    });

    return tr.setNodeMarkup(position, schema.nodes[type], {
      align,
    });
  }
}
